import { TypePolicy } from "@apollo/client";

export const fieldName = "expirationDateTime";

/**
 * Validates if a progress value is valid (not NaN, not Infinity, is a number)
 * @param value The value to check
 * @returns true if the value is valid, false otherwise
 */
export const isValidProgressValue = (value: number): boolean => {
  return Number.isFinite(value) && !Number.isNaN(value);
};

/**
 * Safely calculates a percentage, returning 0 if the total is 0 or invalid
 * @param completed The completed count
 * @param total The total count
 * @returns The percentage (0-100), or 0 if calculation would be invalid
 */
export const safeCalculatePercentage = (completed: number, total: number): number => {
  // If total is 0, null, undefined, or invalid, return 0
  if (!total || total <= 0 || !Number.isFinite(total)) {
    return 0;
  }

  // If completed is invalid, treat as 0
  if (!Number.isFinite(completed) || Number.isNaN(completed)) {
    return 0;
  }

  const percentage = (completed / total) * 100;

  // Verify the result is valid before returning
  if (!isValidProgressValue(percentage)) {
    return 0;
  }

  return Math.ceil(percentage);
};

export default {
  keyFields: ["instanceId"],
  fields: {
    percentSlides: {
      read(_, { readField }) {
        const viewedSlides = Number(readField("viewedSlides"));
        const totalSlides = Number(readField("totalSlides"));
        return safeCalculatePercentage(viewedSlides, totalSlides);
      },
    },
    percentQuizzes: {
      read(_, { readField }) {
        const passedQuizzes = Number(readField("passedQuizzes"));
        const totalQuizzes = Number(readField("totalQuizzes"));
        return safeCalculatePercentage(passedQuizzes, totalQuizzes);
      },
    },
    hoursRemaining: {
      read(_, { readField }) {
        const viewedSeconds = Number(readField("viewedSeconds"));
        const totalSeconds = Number(readField("totalSeconds"));

        // If totalSeconds is 0 or invalid, return 0
        if (!totalSeconds || totalSeconds <= 0 || !Number.isFinite(totalSeconds)) {
          return 0;
        }

        // If viewedSeconds is invalid, treat as 0
        const validViewedSeconds = Number.isFinite(viewedSeconds) && viewedSeconds >= 0 ? viewedSeconds : 0;

        // Calculate remaining seconds, ensuring it's not negative
        const remainingSeconds = Math.max(0, totalSeconds - validViewedSeconds);
        return Math.floor(remainingSeconds / 3600);
      },
    },
  },
} as TypePolicy;
